import { useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Animations Section - CSS animation examples
 */
export default function AnimationsSection({ theme }) {
  const isDark = theme === 'dark';
  const [expanded, setExpanded] = useState(false);
  const [checked, setChecked] = useState(false);

  return (
    <div className="space-y-8">
      <div>
        <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Animations
        </h2>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Smooth transitions and micro-interactions
        </p>
      </div>

      {/* Interactive Animations */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Interactive Animations
        </h3>

        <div className="space-y-4">
          {/* Accordion */}
          <button
            onClick={() => setExpanded(!expanded)}
            className={`w-full p-4 rounded-lg text-left transition-all ${
              isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
            } border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
          >
            <div className="flex items-center justify-between">
              <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Click to expand
              </span>
              <ChevronDown
                className={`transition-transform duration-300 ${expanded ? 'rotate-180' : ''} ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}
                size={20}
              />
            </div>
            <div className={`overflow-hidden transition-all duration-300 ${
              expanded ? 'max-h-40 mt-3' : 'max-h-0'
            }`}>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                This content smoothly expands and collapses with a height transition.
                Notice how the chevron icon rotates as well.
              </p>
            </div>
          </button>

          {/* Checkbox with animation */}
          <button
            onClick={() => setChecked(!checked)}
            className={`flex items-center gap-3 p-4 rounded-lg transition-all ${
              checked
                ? isDark ? 'bg-blue-900/30 border-blue-500' : 'bg-blue-50 border-blue-300'
                : isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border-2`}
          >
            <div className={`w-6 h-6 rounded flex items-center justify-center transition-all ${
              checked ? 'bg-blue-600 scale-110' : isDark ? 'bg-gray-700' : 'bg-gray-200'
            }`}>
              {checked && <Check className="text-white" size={16} strokeWidth={3} />}
            </div>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Animated checkbox
            </span>
          </button>
        </div>
      </div>

      {/* Hover Effects */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Hover Effects
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className={`p-6 rounded-lg transition-all hover:scale-105 hover:shadow-xl cursor-pointer ${
            isDark ? 'bg-gray-800' : 'bg-white'
          } border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Hover to scale up
            </p>
          </div>
          <div className={`p-6 rounded-lg transition-all hover:-translate-y-2 hover:shadow-xl cursor-pointer ${
            isDark ? 'bg-gray-800' : 'bg-white'
          } border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Hover to lift up
            </p>
          </div>
        </div>
      </div>

      {/* Loading Spinner */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Loading Indicators
        </h3>
        <div className="flex items-center gap-6">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <div className="w-8 h-8 border-4 border-green-200 border-t-green-600 rounded-full animate-spin" />
          <div className="w-8 h-8 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin" />
        </div>
      </div>

      {/* Pulse Effects */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Pulse & Bounce
        </h3>
        <div className="flex items-center gap-6">
          <div className="w-4 h-4 bg-blue-500 rounded-full animate-pulse" />
          <div className="w-4 h-4 bg-green-500 rounded-full animate-bounce" />
          <div className={`px-3 py-1 rounded-full animate-pulse ${
            isDark ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-600'
          }`}>
            <span className="text-sm font-medium">New</span>
          </div>
        </div>
      </div>
    </div>
  );
}
